import React, { useCallback, useRef, useState } from 'react';
import { useChatFeedbackLock } from '../FarChat/ChatFeedbackOverlays';
import type { ChatSession } from './types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import useDragDropUpload from '../FarChat/useDragDropUpload';
import { prepareInAppCompletionSound } from './completionNotifications';
import { HOME_CHAT_COMPLETION_SOUND_EVENT, HOME_CHAT_COMPLETION_SOUND_STORAGE_KEY } from './HomeChatCompletionReconciler';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { mergeHomeChatConversation } from './homeChatConversation';
import { GENERIC_TURN_ERROR_COPY, friendlyTurnErrorMessage, isTurnInProgressError, isExpectedCancellationError } from './HomeChatTurnErrors';
import { useHomeChatPanelState } from './useHomeChatPanelState';
import { useHomeChatHydration } from './useHomeChatHydration';
import { useHomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';
import { useHomeChatTurnSubmission } from './useHomeChatTurnSubmission';
import { useHomeChatSessions } from './useHomeChatSessions';
import { useHomeChatTurnEditing } from './useHomeChatTurnEditing';
import { useHomeChatComposer } from './useHomeChatComposer';
import { useHomeChatSessionActions } from './useHomeChatSessionActions';
import { useHomeChatWorkflowHandoffs } from './useHomeChatWorkflowHandoffs';
import { useHomeChatCardActions } from './useHomeChatCardActions';
import { HomeChatPanelView } from './HomeChatPanelView';
import { useDocumentAssistant } from './DocumentAssistantContext';
import { HomeChatHttpError, postCompanionRoute, type CompanionRouteRequest } from './api';
import { makeRequestId } from './homeChatSessionUtils';
import type { FarRegime } from '../FarChat/types';
import { extractGpcRouterResponse } from './gpcHandoff';
import { extractMrRouterResponse } from './mrHandoff';
import { extractConstructionSowRouterResponse } from './constructionSowHandoff';
import { extractServicesPwsRouterResponse } from './servicesPwsHandoff';

export type { ConstrainedWorkflowEntryState } from './HomeChatPanel.types';
export { mergeHomeChatConversation } from './homeChatConversation';
export { GENERIC_TURN_ERROR_COPY, friendlyTurnErrorMessage, isTurnInProgressError, isExpectedCancellationError } from './HomeChatTurnErrors';


const HomeChatPanel: React.FC<Props> = ({
  initialMessage = null,
  initialSessionId = null,
  initialMrChatView = null,
  initialMrObserverNotificationId = null,
  initialMrNotificationBoundResumeId = null,
  initialGpcRouter = null,
  initialConstructionSowChatView = null,
  initialWorkflowOnlyChoice = null,
  initialWorkflowOnlyPurchaseGuidance = null,
  initialWorkflowOnlyGuidance = null,
  initialWorkflowOnlyMessage = null,
  constrainedWorkflowEntry = null,
  onRetryConstrainedWorkflowEntry,
  onBackConstrainedWorkflowEntry,
  workflowOnly = false,
  workflowOnlyAudience = null,
  allowedWorkflowTypes,

  displayMode = 'workspace',
  enableCompletionSound = false,
  pageContext,
  onActiveSessionIdChange,
  onTurnStart,
  onOpenChatExamples,
  onOpenTraining,
  onOpenWorkflow,
  onOpenMrWorkflow,
  onOpenConstructionSowWorkflow,
  onOpenGeneratedServicesPwsWorkflow,
  onOpenNavigationHandle,
  onStartWorkflowType,
  onOpenCreatedWorkflow,
  onBeforeWorkflowNavigation,
}) => {
  const {
    constrainedOptimisticSessionId,
    setConstrainedOptimisticSessionId,
    mrObserverNotificationId,
    setMrObserverNotificationId,
    mrNotificationBoundResumeId,
    setMrNotificationBoundResumeId,
    workflowOnlyChoice,
    setWorkflowOnlyChoice,
    workflowOnlyPurchaseGuidance,
    setWorkflowOnlyPurchaseGuidance,
    failedPurchaseFollowup,
    setFailedPurchaseFollowup,
    newPurchaseGuidanceFocusId,
    setNewPurchaseGuidanceFocusId,
    workflowOnlyGuidance,
    setWorkflowOnlyGuidance,
    workflowOnlyEntryMessage,
    setWorkflowOnlyEntryMessage,
    isCompanion,
    isMainHomeChatSoundSurface,
    composerPlaceholder,
    sessions,
    setSessions: setRawSessions,
    sessionsLoading,
    setSessionsLoading: setRawSessionsLoading,
    mobileSessionsOpen,
    setMobileSessionsOpen,
    activeSessionId,
    setActiveSessionId,
    gpcChatView,
    setGpcChatView,
    mrChatView,
    setMrChatView,
    mrTurnInFlight,
    setMrTurnInFlight,
    constructionSowChatView,
    setConstructionSowChatView,
    servicesPwsChatView,
    setServicesPwsChatView,
    workflowDocumentHistory,
    setWorkflowDocumentHistory,
    gpcViewsByHomeSessionRef,
    rehydratingGpcSessionId,
    setRehydratingGpcSessionId,
    optimisticGpcOpening,
    setOptimisticGpcOpening,
    pendingGpcRouter,
    setPendingGpcRouter,
    activeSessionIdRef,
    reportedSessionIdRef,
    sessionsRef,
    sessionListRefreshVersionRef,
    messages,
    setMessages,
    turns,
    setTurns,
    chatCards,
    setChatCards,
    busySimilarRequirementsCardId,
    setBusySimilarRequirementsCardId,
    busyWorkflowRecommendationCardId,
    setBusyWorkflowRecommendationCardId,
    busyActionChoiceLabel,
    setBusyActionChoiceLabel,
    busyTrainingActionId,
    setBusyTrainingActionId,
    workflowRecommendationRequestIdRef,
    cardActionRevisionRef,
    pendingIntentChoiceSelection,
    setPendingIntentChoiceSelection,
    pendingIntentChoiceRequestIdRef,
    clearPendingIntentChoiceSelection,
    conversationItems,
    attachments,
    setAttachments,
    inFlight,
    setInFlight,
    notice,
    setNotice,
    soundEnabled,
    setSoundEnabled,
    completionSoundStatus,
    stoppingOperationId,
    setStoppingOperationId,
    stoppingOperationIdRef,
    editingTurnId,
    setEditingTurnId,
    editDraft,
    setEditDraft,
    replacingTurnId,
    setReplacingTurnId,
    branchRevision,
    setBranchRevision,
    pendingRequestId,
    setPendingRequestId,
    pendingRequestIdRef,
    interactionRevisionRef,
    setPendingRequestIdSynced,
    inFlightUserMsg,
    setInFlightUserMsg,
    pendingClarification,
    setPendingClarification,
    restorePendingClarification,
    inFlightClarification,
    setInFlightClarification,
    pendingTopicClarification,
    setPendingTopicClarification,
    maybePlayClarificationSound,
    deliverResponseLlmResultEvents,
    inFlightTopicFocus,
    setInFlightTopicFocus,
    inputContainerRef,
    threadScrollRef,
    scrollThreadToLatest,
    pendingCreatePromiseRef,
    inFlightTitleTimerRef,
    inFlightActivityTimerRef,
    firstRunRef,
  } = useHomeChatPanelState({
    constrainedWorkflowEntry,
    initialConstructionSowChatView,
    initialGpcRouter,
    initialMrChatView,
    initialMrNotificationBoundResumeId,
    initialMrObserverNotificationId,
    initialSessionId,
    initialWorkflowOnlyChoice,
    initialWorkflowOnlyGuidance,
    initialWorkflowOnlyMessage,
    initialWorkflowOnlyPurchaseGuidance,
    workflowOnly,
    displayMode,
    enableCompletionSound,
  });

  const feedbackLocked = useChatFeedbackLock();
  const [initialSessionsReady, setInitialSessionsReady] = useState(false);
  const setSessionsLoading = useCallback((loading: React.SetStateAction<boolean>) => {
    setRawSessionsLoading(loading);
    if (loading === false) setInitialSessionsReady(true);
  }, [setRawSessionsLoading]);

  // A returned answer remains terminal even if feedback blocks the next poll
  // or a stale activity snapshot still says Working. Keep operation ownership
  // so a later, distinct operation is never hidden with the completed one.
  const completedRequestIdsRef = useRef(new Set<string>());
  const completedOperationIdsRef = useRef(new Set<string>());
  const withoutCompletedActivity = useCallback((session: ChatSession): ChatSession => {
    const activity = session.activity;
    return activity?.kind === 'home_chat_turn' && (
      completedOperationIdsRef.current.has(activity.operation_id)
      || (activity.client_request_id
        && completedRequestIdsRef.current.has(activity.client_request_id))
    ) ? { ...session, activity: null } : session;
  }, []);
  const setSessions = useCallback<React.Dispatch<React.SetStateAction<ChatSession[]>>>(
    (update) => setRawSessions((previous) => {
      const next = typeof update === 'function' ? update(previous) : update;
      const filtered = next.map(withoutCompletedActivity);
      return filtered.every((session, index) => session === next[index]) ? next : filtered;
    }),
    [setRawSessions, withoutCompletedActivity],
  );
  const markTurnCompleted = useCallback((sessionId: string | null, requestId?: string | null) => {
    if (requestId) completedRequestIdsRef.current.add(requestId);
    const activity = sessionsRef.current.find((session) => session.session_id === sessionId)?.activity;
    if (activity?.kind === 'home_chat_turn' && (
      !activity.client_request_id || activity.client_request_id === requestId
    )) completedOperationIdsRef.current.add(activity.operation_id);
    setSessions((previous) => previous);
  }, [sessionsRef, setSessions]);
  const deliverCompletedResponse = useCallback((response: unknown) => {
    deliverResponseLlmResultEvents(response);
    if (!response || typeof response !== 'object') return;
    const result = response as { turn_id?: string; session_id?: string; turn?: { turn_id?: string } };
    if (result.turn_id || result.turn?.turn_id) {
      markTurnCompleted(result.session_id || activeSessionIdRef.current, pendingRequestIdRef.current);
    }
  }, [deliverResponseLlmResultEvents, markTurnCompleted, activeSessionIdRef, pendingRequestIdRef]);
  // The editing hook clears clarification only after a successful replacement.
  // Observe that terminal boundary without moving elastic's editing logic.
  const clearClarificationAfterEdit = useCallback<typeof setPendingClarification>((value) => {
    if (value === null && pendingRequestIdRef.current) {
      markTurnCompleted(activeSessionIdRef.current, pendingRequestIdRef.current);
    }
    setPendingClarification(value);
  }, [setPendingClarification, markTurnCompleted, activeSessionIdRef, pendingRequestIdRef]);

  const {
    chatOpenErrorMessage,

    notify,
    replaceChatCard,
    applyGpcChatView,
    applyMrChatView,
    applyConstructionSowChatView,
    applyServicesPwsChatView,
    hydrateMrBinding,
    hydrateDocumentHistory,
    hydrateGpcState,
    hydrateConstructionSowBinding,
    hydrateServicesPwsBinding,
    isStaleResolve,
    hydrateDirectSearchThread,
    clearPendingIfOwned,
  } = useHomeChatHydration({
    setWorkflowDocumentHistory,
    preserveControllerCards: displayMode === 'companion',
    setWorkflowOnlyChoice,
    setWorkflowOnlyEntryMessage,
    setWorkflowOnlyPurchaseGuidance,
    workflowOnly,
    setNotice,
    setChatCards,
    activeSessionIdRef,
    gpcViewsByHomeSessionRef,
    setRehydratingGpcSessionId,
    setActiveSessionId,
    setGpcChatView,
    setMrChatView,
    setPendingGpcRouter,
    setOptimisticGpcOpening,
    setServicesPwsChatView,
    setConstructionSowChatView,
    pendingRequestIdRef,
    setMessages,
    setAttachments,
    setTurns,
    setBranchRevision,
    setPendingRequestId,
  });

  const {
    refreshSessions,
    reconcileExpectedCancellation,
    clearUnavailableSession,
    scheduleInFlightTitleRefresh,
    refreshAfterTurnDispatch,
  } = useHomeChatSessionReconciliation({
    chatCards,
    setWorkflowOnlyPurchaseGuidance,
    workflowOnlyPurchaseGuidance,
    constrainedWorkflowEntry,
    workflowOnly,
    activeSessionIdRef,
    sessionListRefreshVersionRef,
    sessionsRef,
    setSessionsLoading,
    pendingRequestIdRef,
    setActiveSessionId,
    setSessions,
    notify,
    isStaleResolve,
    interactionRevisionRef,
    hydrateDirectSearchThread,
    setInFlight,
    clearPendingIfOwned,
    setInFlightUserMsg,
    setInFlightClarification,
    setInFlightTopicFocus,
    stoppingOperationIdRef,
    setStoppingOperationId,
    setReplacingTurnId,
    scrollThreadToLatest,
    reportedSessionIdRef,
    setMessages,
    setTurns,
    setChatCards,
    setAttachments,
    setGpcChatView,
    setMrChatView,
    setConstructionSowChatView,
    setRehydratingGpcSessionId,
    onActiveSessionIdChange,
    inFlightTitleTimerRef,
    inFlightActivityTimerRef,
    activeSessionId,
    initialSessionId,
    sessions,
  });

  const {
    sendV2Turn,
    acceptCompanionResponse,
    sendLazyCreateV2Turn,
    runPrimingTurn,
  } = useHomeChatTurnSubmission({
    setNewPurchaseGuidanceFocusId,
    pageContext,
    isStaleResolve,
    deliverResponseLlmResultEvents: deliverCompletedResponse,
    applyConstructionSowChatView,
    applyServicesPwsChatView,
    applyMrChatView,
    hydrateDirectSearchThread,
    scrollThreadToLatest,
    applyGpcChatView,
    setPendingGpcRouter,
    replaceChatCard,
    setPendingClarification,
    maybePlayClarificationSound,
    setPendingTopicClarification,
    setTurns,
    interactionRevisionRef,
    setInFlightUserMsg,
    setInFlightClarification,
    setInFlightTopicFocus,
    setPendingRequestIdSynced,
    setInFlight,
    activeSessionIdRef,
    setActiveSessionId,
    scheduleInFlightTitleRefresh,
    refreshAfterTurnDispatch,
    setSessions,
    refreshSessions,
    reconcileExpectedCancellation,
    setMessages,
    notify,
    clearPendingIfOwned,
  });

  const {
    insertPendingPlaceholder,
    handleNewChat,
    ensureSession,
    progress,
    gpcHandoffProgressLabel,
    handleSelectSession,
  } = useHomeChatSessions({
    preserveControllerCards: displayMode === 'companion',
    chatOpenErrorMessage,
    initialConstructionSowChatView,
    initialMrChatView,
    initialMrNotificationBoundResumeId,
    initialMrObserverNotificationId,
    workflowOnly,
    setMrNotificationBoundResumeId,
    setMrObserverNotificationId,
    setWorkflowOnlyChoice,
    setWorkflowOnlyEntryMessage,
    setWorkflowOnlyPurchaseGuidance,
    sessionsLoading: sessionsLoading || Boolean(initialSessionId && !initialSessionsReady && !workflowOnly),
    firstRunRef,
    initialMessage,
    initialSessionId,
    activeSessionIdRef,
    setGpcChatView,
    setMrChatView,
    setConstructionSowChatView,
    setServicesPwsChatView,
    sessionsRef,
    setRehydratingGpcSessionId,
    setActiveSessionId,
    setMessages,
    setAttachments,
    setTurns,
    setChatCards,
    interactionRevisionRef,
    setBranchRevision,
    hydrateMrBinding,
    hydrateDocumentHistory,
    hydrateServicesPwsBinding,
    hydrateConstructionSowBinding,
    restorePendingClarification,
    hydrateGpcState,
    notify,
    clearUnavailableSession,
    runPrimingTurn,
    setSessions,
    cardActionRevisionRef,
    workflowRecommendationRequestIdRef,
    setBusyWorkflowRecommendationCardId,
    setBusySimilarRequirementsCardId,
    clearPendingIntentChoiceSelection,
    setPendingGpcRouter,
    setInFlight,
    setPendingRequestIdSynced,
    setInFlightUserMsg,
    setPendingClarification,
    setInFlightClarification,
    setPendingTopicClarification,
    setInFlightTopicFocus,
    inputContainerRef,
    activeSessionId,
    pendingCreatePromiseRef,
    refreshSessions,
    setPendingRequestId,
    setReplacingTurnId,
    mrChatView,
    pendingIntentChoiceSelection,
    pendingRequestId,
    gpcViewsByHomeSessionRef,
  });


  const handleToggleCompletionSound = useCallback(() => {
    setSoundEnabled((enabled) => {
      const next = !enabled;
      try { window.localStorage.setItem(HOME_CHAT_COMPLETION_SOUND_STORAGE_KEY, String(next)); } catch { /* preference is best-effort */ }
      window.dispatchEvent(new Event(HOME_CHAT_COMPLETION_SOUND_EVENT));
      // The speaker itself is an explicit gesture. Prime here as well as in
      // the page-lifetime reconciler so the preference does not depend on
      // event-listener ordering or on a later completion poll.
      if (next) void prepareInAppCompletionSound();
      return next;
    });
  }, []);

  // This is intentionally owned by the outer main Home Chat panel rather
  // than any embedded controller. It therefore remains reachable while the
  // panel switches among ordinary, MR, GPC, Construction, and Services
  // views, yet cannot appear in a workflow companion/sidebar.
  const completionSoundControl = isMainHomeChatSoundSurface ? (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <Tooltip
        title={!soundEnabled
          ? 'Turn on a sound when a Home Chat result is ready'
          : completionSoundStatus === 'needs-activation'
            ? 'Completion sound needs one click or keypress in this tab before it can play'
          : completionSoundStatus === 'unavailable'
              ? 'Completion sound is unavailable in this browser or device state'
              : 'Turn off the completion sound'}
        placement="bottom"
      >
        <IconButton
          size="small"
          aria-label={soundEnabled ? 'Disable completion sound' : 'Enable completion sound'}
          aria-pressed={soundEnabled}
          onClick={handleToggleCompletionSound}
        >
          {soundEnabled
            ? <VolumeUpOutlinedIcon fontSize="small" />
            : <VolumeOffOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  const {
    activeSession,
    editableLatestTurnId,
    processingStopControl,
    handleBeginEdit,
    handleCancelEdit,
    handleSaveAndResendEdit,
  } = useHomeChatTurnEditing({
    activeSessionId,
    sessions,
    turns,
    inFlight,
    stoppingOperationId,
    stoppingOperationIdRef,
    setStoppingOperationId,
    reconcileExpectedCancellation,
    pendingRequestIdRef,
    refreshSessions,
    notify,
    setEditingTurnId,
    setEditDraft,
    editingTurnId,
    editDraft,
    interactionRevisionRef,
    setReplacingTurnId,
    setTurns,
    setInFlight,
    setInFlightUserMsg,
    setInFlightClarification,
    setInFlightTopicFocus,
    setPendingRequestIdSynced,
    scrollThreadToLatest,
    branchRevision,
    refreshAfterTurnDispatch,
    isStaleResolve,
    activeSessionIdRef,
    setPendingTopicClarification,
    setPendingClarification: clearClarificationAfterEdit,
    maybePlayClarificationSound,
    setBranchRevision,
    hydrateDirectSearchThread,
    clearPendingIfOwned,
  });

  const {
    handleSend,
    handleResolveClarification,
    handleResolveTopicFocus,
  } = useHomeChatComposer({
    inFlight,
    pendingClarification,
    onTurnStart,
    interactionRevisionRef,
    setInFlightUserMsg,
    setInFlightClarification,
    setInFlightTopicFocus,
    setPendingRequestIdSynced,
    setTurns,
    setPendingClarification,
    setInFlight,
    scrollThreadToLatest,
    activeSessionId,
    insertPendingPlaceholder,
    scheduleInFlightTitleRefresh,
    messages,
    turns,
    ensureSession,
    sendLazyCreateV2Turn,
    refreshAfterTurnDispatch,
    isStaleResolve,
    activeSessionIdRef,
    setActiveSessionId,
    setSessions,
    sendV2Turn,
    refreshSessions,
    reconcileExpectedCancellation,
    setMessages,
    notify,
    clearPendingIfOwned,
    runPrimingTurn,
    pendingTopicClarification,
    setPendingTopicClarification,
  });

  const {
    handleRename,
    handleDelete,
    handleAttach,
    handleRemoveAttachment,
  } = useHomeChatSessionActions({
    refreshSessions,
    notify,
    setSessions,
    activeSessionId,
    activeSessionIdRef,
    setActiveSessionId,
    setGpcChatView,
    setMrChatView,
    setConstructionSowChatView,
    setServicesPwsChatView,
    setRehydratingGpcSessionId,
    setMessages,
    setAttachments,
    setTurns,
    setChatCards,
    clearPendingIntentChoiceSelection,
    setInFlight,
    setPendingRequestIdSynced,
    setInFlightUserMsg,
    setPendingClarification,
    setInFlightClarification,
    setPendingTopicClarification,
    setInFlightTopicFocus,
    ensureSession,
  });

  const {
    submitWorkflowOnlyChoice,
    submitNotificationBoundMrTurn,
    rehydrateBoundMrChat,
    submitSectionCHandoff,
    notifyBoundMrQuestions,
    shareAndNotifyBoundMrQuestions,

    submitGpcConfirmation,
    submitBoundGpcTurn,
    submitBoundMrTurn,
    uploadMrFiles,
    getHandoffContext,
    confirmWorkflowNavigation,
    submitBoundConstructionSowTurn,
    uploadConstructionRequirement,
    extractConstructionRequirement,
    removeConstructionRequirement,
    uploadConstructionSupport,
    replaceConstructionSupport,
    removeConstructionSupport,
    openGeneratedConstructionSow,
    submitBoundServicesPwsTurn,
    uploadServicesRequirement,
    extractServicesRequirement,
    removeServicesRequirement,
    uploadServicesSupport,
    replaceServicesSupport,
    removeServicesSupport,
    openGeneratedServicesPws,
    openMrWorkflow,
    openGpcWorkflow,
  } = useHomeChatWorkflowHandoffs({
    mrNotificationBoundResumeId,
    mrObserverNotificationId,
    setMrChatView,
    setMrNotificationBoundResumeId,
    setMrObserverNotificationId,
    setWorkflowOnlyChoice,
    workflowOnlyChoice,
    onStartWorkflowType,
    workflowOnly,
    pendingGpcRouter,
    inFlight,
    setOptimisticGpcOpening,
    setInFlight,
    setPendingRequestIdSynced,
    activeSessionId,
    isStaleResolve,
    deliverResponseLlmResultEvents,
    applyGpcChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyGpcChatView(view); },
    refreshSessions,
    setPendingGpcRouter,
    setPendingClarification,
    maybePlayClarificationSound,
    setPendingTopicClarification,
    notify,
    setActiveSessionId,
    setTurns,
    clearPendingIfOwned,
    inputContainerRef,
    gpcChatView,
    mrChatView,
    setMrTurnInFlight,
    applyMrChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyMrChatView(view); },
    activeSessionIdRef,
    interactionRevisionRef,
    displayMode,
    onBeforeWorkflowNavigation,
    applyConstructionSowChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyConstructionSowChatView(view); },
    constructionSowChatView,
    onOpenConstructionSowWorkflow,
    applyServicesPwsChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyServicesPwsChatView(view); },
    servicesPwsChatView,
    onOpenGeneratedServicesPwsWorkflow,
    onOpenMrWorkflow,
    onOpenWorkflow,
  });

  const {
    handleSelectPurchaseGuidance,
    handleSelectMissionPartnerGuidance,
    currentPurchaseClarification,
    currentRaFollowupCard,
    handlePurchaseClarification,

    handleSelectSimilarRequirementsType,
    handleOpenCreatedWorkflow,
    handleCancelSimilarRequirements,
    handleSelectTrainingAction,
    handleSelectWorkflowRecommendation,
    handleSelectActionChoice,
    handleSelectAmbiguousIntentChoice,
    handleOpenSimilarRequirementsResult,
  } = useHomeChatCardActions({
    scrollThreadToLatest,
    sendV2Turn,
    turns,
    chatCards,
    inFlight,
    setFailedPurchaseFollowup,
    setNewPurchaseGuidanceFocusId,
    setWorkflowOnlyChoice,
    setWorkflowOnlyPurchaseGuidance,
    workflowOnlyChoice,
    workflowOnlyPurchaseGuidance,
    refreshSessions,
    workflowOnly,
    workflowOnlyAudience,
    activeSessionId,
    setBusySimilarRequirementsCardId,
    setChatCards,
    notify,
    activeSessionIdRef,
    isCompanion,
    onOpenTraining,
    busyTrainingActionId,
    setBusyTrainingActionId,
    setNotice,
    workflowRecommendationRequestIdRef,
    confirmWorkflowNavigation,
    getHandoffContext,
    cardActionRevisionRef,
    setBusyWorkflowRecommendationCardId,
    onStartWorkflowType,
    setBusyActionChoiceLabel,
    applyConstructionSowChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyConstructionSowChatView(view); },
    applyServicesPwsChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyServicesPwsChatView(view); },
    applyMrChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyMrChatView(view); },
    applyGpcChatView: (view) => { if (documentCompanion) setControllerAvailable(true); applyGpcChatView(view); },
    setTurns,
    replaceChatCard,
    hydrateDirectSearchThread,
    pendingIntentChoiceRequestIdRef,
    messages,
    setPendingIntentChoiceSelection,
    setPendingRequestIdSynced,
    setInFlight,
    setInFlightUserMsg,
    setInFlightClarification,
    setInFlightTopicFocus,
    setOptimisticGpcOpening,
    isStaleResolve,
    setPendingGpcRouter,
    setMessages,
    clearPendingIfOwned,
    clearPendingIntentChoiceSelection,
    onOpenNavigationHandle,
    onOpenCreatedWorkflow,
  });


  // Blank-popup regression fix (sondam/far-chatbot): the homepage-submit
  // priming flow sets `inFlight=true`, `pendingRequestId`, and
  // `inFlightUserMsg` BEFORE the v2 lazy-create response resolves — but
  // `activeSessionId` does not become non-null until that response
  // returns. If we treated the absence of `activeSessionId` + empty
  // `messages` as "empty state", the placeholder ("Start a new chat or
  // pick one from the left") would render for the entire LLM round-trip,
  // hiding the in-flight user bubble and StepProgressChecklist that the
  // else-branch is set up to render. Net effect: the popup looked blank
  // until the answer arrived. Suppress the empty state whenever there's
  // any in-flight signal, queued turn, or rendered turn — the same set
  // of conditions that gate the in-flight UI below at line ~1107.
  const showEmptyState =
    activeSessionId === null &&
    messages.length === 0 &&
    turns.length === 0 &&
    chatCards.length === 0 &&
    !pendingGpcRouter &&
    !inFlight &&
    !pendingRequestId &&
    !inFlightUserMsg &&
    // A live clarification card occupies the pane in place of an answer.
    !pendingClarification &&
    // A live topic-focus card likewise occupies the pane.
    !pendingTopicClarification;

  // Drag-and-drop: reuse the same upload path as the click-upload button.
  // Drop disabled while a turn is in flight. If there's no active session,
  // handleAttach will eagerly create one server-side before uploading.
  const { dragHandlers, isDragging } = useDragDropUpload({
    disabled: workflowOnly || inFlight || !!currentPurchaseClarification,
    onFiles: async (files) => {
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        await handleAttach(f);
      }
    },
  });
  const documentAssistant = useDocumentAssistant();
  const documentCompanion = isCompanion && !!documentAssistant.registration?.capability.activeEditor;
  const [controllerAvailable, setControllerAvailable] = useState(false);
  const [controllerOpen, setControllerOpen] = useState(false);
  React.useEffect(() => { setControllerAvailable(false); setControllerOpen(false); }, [documentAssistant.registration?.registrationId]);
  React.useEffect(() => { setControllerOpen(false); }, [documentAssistant.openRevision]);
  const sendDocumentMessage = documentAssistant.sendMessage;
  const sourceConversation = useRef<{ scope: string; flowId: string } | null>(null);
  const sourceScope = JSON.stringify([documentAssistant.scopeUser, activeSessionId]);
  if (sourceConversation.current?.scope !== sourceScope) sourceConversation.current = null;
  const sourceView = [servicesPwsChatView, constructionSowChatView].find(view =>
    view?.home_chat_session_id === activeSessionId && view?.cursor === 'handoff_ready' && view.workflow?.flow_id);
  const historySource = workflowDocumentHistory?.sessionId === activeSessionId
    ? workflowDocumentHistory.items[0]?.flow_id : null;
  const sourceViewFlowId = sourceView?.workflow?.flow_id;
  const sourceFlowId = typeof sourceViewFlowId === 'string' ? sourceViewFlowId : historySource;
  if (!isCompanion && activeSessionId && sourceFlowId) sourceConversation.current = { scope: sourceScope, flowId: sourceFlowId };
  const mainSourceFlowId = !isCompanion ? sourceConversation.current?.flowId : null;
  const companionFlowId = documentAssistant.flowId || mainSourceFlowId;
  const handleDocumentSend = useCallback(async (message: string, intent?: 'edit' | 'question', continuation?: Pick<CompanionRouteRequest, 'regime' | 'final_question' | 'focus_token' | 'topic_focus'>) => {
    onTurnStart?.();
    await sendDocumentMessage(message, {
      generalAuthorized: true, sessionId: activeSessionIdRef.current, explicitIntent: intent, continuation,
      onSessionAllocated: sessionId => { activeSessionIdRef.current = sessionId; setActiveSessionId(sessionId); onActiveSessionIdChange?.(sessionId); },
      onConversation: async (sessionId, response) => {
        activeSessionIdRef.current = sessionId;
        setActiveSessionId(sessionId);
        onActiveSessionIdChange?.(sessionId, { bindingManagedByServer: true });
        const gpc = extractGpcRouterResponse(response);
        const controllerResponse = (gpc && gpc.kind === 'chat') || extractMrRouterResponse(response) || extractConstructionSowRouterResponse(response) || extractServicesPwsRouterResponse(response);
        if (controllerResponse) setControllerAvailable(true);
        await acceptCompanionResponse(response, sessionId);
        const envelope = response as { turn?: { feedbackRequest?: { promptId?: string } }; feedbackRequest?: { promptId?: string } };
        const feedbackRequired = !!(envelope?.turn?.feedbackRequest?.promptId || envelope?.feedbackRequest?.promptId);
        // A prompted answer is already complete. Protected reads are blocked
        // until feedback is submitted and must not replace its live marker.
        if (!controllerResponse && !feedbackRequired) await hydrateDirectSearchThread(sessionId, () => activeSessionIdRef.current === sessionId);
        if (!feedbackRequired) void refreshSessions();
      },
    });
  }, [sendDocumentMessage, activeSessionIdRef, setActiveSessionId, onTurnStart, onActiveSessionIdChange, acceptCompanionResponse, hydrateDirectSearchThread, refreshSessions, scrollThreadToLatest]);
  const handleOffstageCompanionSend = useCallback(async (message: string, continuation?: Pick<CompanionRouteRequest, 'regime' | 'final_question' | 'focus_token' | 'topic_focus'>) => {
    const flow = companionFlowId;
    if (!flow || inFlight) return;
    onTurnStart?.();
    const requestId = makeRequestId();
    setPendingRequestIdSynced(requestId);
    setInFlight(true);
    setInFlightUserMsg(message);
    try {
      const result = await postCompanionRoute({ flow_id: flow, session_id: activeSessionIdRef.current, message, client_request_id: requestId, ...continuation });
      if (isStaleResolve(requestId)) return;
      if (result.kind === 'conversation') {
        activeSessionIdRef.current = result.session_id;
        setActiveSessionId(result.session_id);
        onActiveSessionIdChange?.(result.session_id, { bindingManagedByServer: true });
        await acceptCompanionResponse(result.response, result.session_id, requestId);
      } else {
        const explanation = result.kind === 'edit_current_document' ? 'Open Manual Edit or Generate SOW/PWS to edit this document.' : result.message;
        setMessages(current => [...current, { role: 'user', content: message }, { role: 'assistant', content: explanation }]);
      }
    } catch (error) {
      if (!isStaleResolve(requestId)) {
        if (error instanceof HomeChatHttpError && error.body && typeof error.body === 'object') {
          const detail = (error.body as { detail?: { phase?: string; session_id?: string } }).detail;
          if (detail?.phase === 'companion_binding' && detail.session_id) {
            activeSessionIdRef.current = detail.session_id;
            setActiveSessionId(detail.session_id);
            onActiveSessionIdChange?.(detail.session_id);
          }
        }
        notify('error', friendlyTurnErrorMessage(error));
      }
    } finally {
      if (!isStaleResolve(requestId)) { setInFlight(false); setInFlightUserMsg(null); setPendingRequestIdSynced(null); }
    }
  }, [companionFlowId, activeSessionIdRef, setActiveSessionId, setInFlight, setInFlightUserMsg, setMessages, inFlight, onTurnStart, onActiveSessionIdChange, acceptCompanionResponse, isStaleResolve, notify, setPendingRequestIdSynced]);
  const resolveDocumentClarification = (question: string, regime: FarRegime) => {
    const original = pendingClarification?.original_query || question;
    setPendingClarification(null);
    const continuation = { final_question: question, regime };
    if (documentCompanion) void handleDocumentSend(original, 'question', continuation);
    else void handleOffstageCompanionSend(original, continuation);
  };
  const resolveDocumentTopic = (topic: string) => {
    const pending = pendingTopicClarification;
    if (!pending) return;
    setPendingTopicClarification(null);
    const continuation = { final_question: pending.clarification || pending.original_query, focus_token: pending.focus_token, topic_focus: topic, regime: pending.regime };
    if (documentCompanion) void handleDocumentSend(pending.original_query, 'question', continuation);
    else void handleOffstageCompanionSend(pending.original_query, continuation);
  };
  return <HomeChatPanelView
    workflowDocumentHistory={workflowDocumentHistory}
    documentCompanion={documentCompanion}
    controllerAvailable={controllerAvailable}
    controllerOpen={controllerOpen}
    onOpenController={() => setControllerOpen(true)}
    onReturnDocument={() => setControllerOpen(false)}
    onDocumentSend={handleDocumentSend}
    currentRaFollowupCard={currentRaFollowupCard}
    messages={messages}
    mrNotificationBoundResumeId={mrNotificationBoundResumeId}
    mrObserverNotificationId={mrObserverNotificationId}
    notifyBoundMrQuestions={notifyBoundMrQuestions}
    onBackConstrainedWorkflowEntry={onBackConstrainedWorkflowEntry}
    onRetryConstrainedWorkflowEntry={onRetryConstrainedWorkflowEntry}
    rehydrateBoundMrChat={rehydrateBoundMrChat}
    shareAndNotifyBoundMrQuestions={shareAndNotifyBoundMrQuestions}
    submitNotificationBoundMrTurn={submitNotificationBoundMrTurn}
    submitSectionCHandoff={submitSectionCHandoff}
    submitWorkflowOnlyChoice={submitWorkflowOnlyChoice}
    workflowOnlyEntryMessage={workflowOnlyEntryMessage}
    allowedWorkflowTypes={allowedWorkflowTypes}
    constrainedOptimisticSessionId={constrainedOptimisticSessionId}
    constrainedWorkflowEntry={constrainedWorkflowEntry}
    currentPurchaseClarification={currentPurchaseClarification}
    failedPurchaseFollowup={failedPurchaseFollowup}
    handlePurchaseClarification={handlePurchaseClarification}
    handleSelectMissionPartnerGuidance={handleSelectMissionPartnerGuidance}
    handleSelectPurchaseGuidance={handleSelectPurchaseGuidance}
    initialSessionId={initialSessionId}
    newPurchaseGuidanceFocusId={newPurchaseGuidanceFocusId}
    setNewPurchaseGuidanceFocusId={setNewPurchaseGuidanceFocusId}
    workflowOnly={workflowOnly}
    workflowOnlyAudience={workflowOnlyAudience}
    workflowOnlyChoice={workflowOnlyChoice}
    workflowOnlyGuidance={workflowOnlyGuidance}
    workflowOnlyPurchaseGuidance={workflowOnlyPurchaseGuidance}
    onOpenChatExamples={onOpenChatExamples}
    rehydratingGpcSessionId={rehydratingGpcSessionId}
    activeSessionId={activeSessionId}
    sessions={sessions}
    setMobileSessionsOpen={setMobileSessionsOpen}
    handleSelectSession={handleSelectSession}
    handleNewChat={handleNewChat}
    handleRename={handleRename}
    handleDelete={handleDelete}
    sessionsLoading={sessionsLoading}
    isCompanion={isCompanion}
    mobileSessionsOpen={mobileSessionsOpen && !feedbackLocked}
    mrChatView={mrChatView}
    constructionSowChatView={constructionSowChatView}
    servicesPwsChatView={servicesPwsChatView}
    gpcChatView={gpcChatView}
    optimisticGpcOpening={optimisticGpcOpening}
    pendingIntentChoiceSelection={pendingIntentChoiceSelection}
    displayMode={displayMode}
    submitBoundMrTurn={submitBoundMrTurn}
    activeSessionIdRef={activeSessionIdRef}
    uploadMrFiles={uploadMrFiles}
    openMrWorkflow={openMrWorkflow}
    mrTurnInFlight={mrTurnInFlight}
    inFlight={inFlight}
    pendingRequestId={pendingRequestId}
    processingStopControl={processingStopControl}
    completionSoundControl={completionSoundControl}
    submitBoundServicesPwsTurn={submitBoundServicesPwsTurn}
    uploadServicesRequirement={uploadServicesRequirement}
    extractServicesRequirement={extractServicesRequirement}
    uploadServicesSupport={uploadServicesSupport}
    removeServicesRequirement={removeServicesRequirement}
    replaceServicesSupport={replaceServicesSupport}
    removeServicesSupport={removeServicesSupport}
    openGeneratedServicesPws={openGeneratedServicesPws}
    submitBoundConstructionSowTurn={submitBoundConstructionSowTurn}
    uploadConstructionRequirement={uploadConstructionRequirement}
    extractConstructionRequirement={extractConstructionRequirement}
    uploadConstructionSupport={uploadConstructionSupport}
    removeConstructionRequirement={removeConstructionRequirement}
    replaceConstructionSupport={replaceConstructionSupport}
    removeConstructionSupport={removeConstructionSupport}
    openGeneratedConstructionSow={openGeneratedConstructionSow}
    submitBoundGpcTurn={submitBoundGpcTurn}
    openGpcWorkflow={openGpcWorkflow}
    gpcHandoffProgressLabel={gpcHandoffProgressLabel}
    notice={notice}
    setNotice={setNotice}
    dragHandlers={dragHandlers}
    threadScrollRef={threadScrollRef}
    showEmptyState={showEmptyState}
    conversationItems={conversationItems}
    busySimilarRequirementsCardId={busySimilarRequirementsCardId}
    busyWorkflowRecommendationCardId={busyWorkflowRecommendationCardId}
    handleSelectSimilarRequirementsType={handleSelectSimilarRequirementsType}
    handleCancelSimilarRequirements={handleCancelSimilarRequirements}
    handleOpenSimilarRequirementsResult={handleOpenSimilarRequirementsResult}
    handleSelectAmbiguousIntentChoice={handleSelectAmbiguousIntentChoice}
    handleSelectWorkflowRecommendation={handleSelectWorkflowRecommendation}
    handleOpenCreatedWorkflow={handleOpenCreatedWorkflow}
    handleSelectActionChoice={handleSelectActionChoice}
    busyActionChoiceLabel={busyActionChoiceLabel}
    chatCards={chatCards}
    editableLatestTurnId={editableLatestTurnId}
    busyTrainingActionId={busyTrainingActionId}
    onOpenTraining={onOpenTraining}
    handleSelectTrainingAction={handleSelectTrainingAction}
    handleBeginEdit={handleBeginEdit}
    editingTurnId={editingTurnId}
    editDraft={editDraft}
    setEditDraft={setEditDraft}
    handleSaveAndResendEdit={handleSaveAndResendEdit}
    handleCancelEdit={handleCancelEdit}
    replacingTurnId={replacingTurnId}
    progress={progress}
    turns={turns}
    activeSession={activeSession}
    pendingGpcRouter={pendingGpcRouter}
    submitGpcConfirmation={submitGpcConfirmation}
    pendingClarification={pendingClarification}
    pendingTopicClarification={pendingTopicClarification}
    inFlightUserMsg={inFlightUserMsg}
    inFlightClarification={inFlightClarification}
    inFlightTopicFocus={inFlightTopicFocus}
    handleResolveClarification={documentCompanion || !!mainSourceFlowId ? resolveDocumentClarification : handleResolveClarification}
    handleResolveTopicFocus={documentCompanion || !!mainSourceFlowId ? resolveDocumentTopic : handleResolveTopicFocus}
    attachments={attachments}
    handleRemoveAttachment={handleRemoveAttachment}
    inputContainerRef={inputContainerRef}
    handleSend={(isCompanion && documentAssistant.flowId) || mainSourceFlowId
      ? (message, finalQuestion, regime) => handleOffstageCompanionSend(message, finalQuestion ? { final_question: finalQuestion, ...(regime ? { regime } : {}) } : undefined)
      : handleSend}
    handleAttach={handleAttach}
    composerPlaceholder={composerPlaceholder}
    isDragging={isDragging}
  />;
};

export default HomeChatPanel;
